import React, { useEffect, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import { db, secondaryAuth } from '../firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { createUserWithEmailAndPassword, updatePassword, signOut as firebaseSignOut } from 'firebase/auth';
import { Plus, Pencil, Trash2, X, Link, ExternalLink, Eye, EyeOff, KeyRound } from 'lucide-react';

const EMPTY = { name: '', phone: '', vehicle: '', plateNumber: '', status: 'inactive', email: '', password: '' };

export default function AdminCouriers() {
  const [couriers, setCouriers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showPassModal, setShowPassModal] = useState(false);
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 5000); };

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'couriers'), orderBy('name', 'asc')), snap => {
      setCouriers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  const openAdd = () => { setForm(EMPTY); setEditId(null); setShowPass(false); setShowModal(true); };
  const openEdit = (c) => {
    setForm({ name: c.name, phone: c.phone || '', vehicle: c.vehicle || '', plateNumber: c.plateNumber || '', status: c.status || 'inactive', email: c.email || '', password: '' });
    setEditId(c.id); setShowPass(false); setShowModal(true);
  };
  const openChangePass = (c) => { setSelectedCourier(c); setNewPassword(''); setShowPassModal(true); };

  const handleSave = async () => {
    if (!form.name || !form.email) { showToast('Nama dan email wajib diisi', 'error'); return; }
    if (!editId && form.password.length < 6) { showToast('Password minimal 6 karakter', 'error'); return; }
    setSaving(true);
    try {
      const firestoreData = {
        name: form.name, phone: form.phone, vehicle: form.vehicle,
        plateNumber: form.plateNumber, status: form.status, email: form.email,
        updatedAt: serverTimestamp()
      };

      if (editId) {
        // Edit data — hanya update Firestore, tidak ubah password
        await updateDoc(doc(db, 'couriers', editId), firestoreData);
        showToast('✅ Data kurir diperbarui');
      } else {
        // Buat akun baru di Firebase Auth pakai secondaryAuth
        // Trik ini agar sesi admin tidak ter-logout
        const userCred = await createUserWithEmailAndPassword(secondaryAuth, form.email, form.password);
        // Langsung sign out dari secondary app supaya tidak tercampur
        await firebaseSignOut(secondaryAuth);

        // Simpan data + uid ke Firestore
        await addDoc(collection(db, 'couriers'), {
          ...firestoreData,
          uid: userCred.user.uid,
          createdAt: serverTimestamp()
        });

        showToast(`✅ Akun kurir dibuat! Email: ${form.email} | Password: ${form.password}`);
      }
      setShowModal(false);
    } catch (e) {
      if (e.code === 'auth/email-already-in-use') {
        showToast('❌ Email sudah dipakai akun lain di Firebase', 'error');
      } else if (e.code === 'auth/weak-password') {
        showToast('❌ Password terlalu lemah (min 6 karakter)', 'error');
      } else {
        showToast('❌ Gagal: ' + e.message, 'error');
      }
    }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) { showToast('Password minimal 6 karakter', 'error'); return; }
    setSaving(true);
    try {
      // Login ke secondary app dulu pakai email kurir, lalu update password
      // Cara ini butuh password lama → gunakan Firebase Admin SDK untuk produksi
      // Solusi sederhana: update via Firestore saja sebagai catatan admin
      await updateDoc(doc(db, 'couriers', selectedCourier.id), {
        passwordNote: `[Admin set: ${new Date().toLocaleDateString('id-ID')}]`,
        updatedAt: serverTimestamp()
      });
      showToast(`✅ Catatan password disimpan. Untuk reset password aktual, gunakan Firebase Console → Authentication → cari email kurir → Reset password`);
      setShowPassModal(false);
    } catch (e) {
      showToast('❌ Gagal: ' + e.message, 'error');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus kurir ini?')) return;
    await deleteDoc(doc(db, 'couriers', id));
    showToast('Kurir dihapus');
  };

  const toggleStatus = async (courier) => {
    const newStatus = courier.status === 'active' ? 'inactive' : 'active';
    await updateDoc(doc(db, 'couriers', courier.id), { status: newStatus });
    showToast(`Kurir ${newStatus === 'active' ? 'diaktifkan' : 'dinonaktifkan'}`);
  };

  const copyTrackingLink = (id) => {
    const url = `${window.location.origin}/tracking/${id}`;
    navigator.clipboard.writeText(url);
    showToast('Link tracking disalin ke clipboard!');
  };

  const activeCouriers = couriers.filter(c => c.status === 'active');

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1>Manajemen Kurir</h1>
            <p>Kelola data kurir dan pantau status keaktifan mereka.</p>
          </div>
          <button className="btn btn-primary" onClick={openAdd}><Plus size={16} /> Tambah Kurir</button>
        </div>

        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '1.5rem' }}>
          {[
            { label: 'Total Kurir', value: couriers.length, color: 'var(--green-800)' },
            { label: 'Sedang Aktif', value: activeCouriers.length, color: '#16a34a' },
            { label: 'Tidak Aktif', value: couriers.length - activeCouriers.length, color: '#94a3b8' },
          ].map((s, i) => (
            <div key={i} className="stat-card">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ color: s.color, fontSize: 22 }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Info box */}
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 12, padding: '14px 16px', marginBottom: '1.25rem', fontSize: 13, color: '#166534' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>🔐 Sistem Login Kurir</div>
          <div style={{ lineHeight: 1.7 }}>
            • Admin mendaftarkan kurir di sini → akun otomatis dibuat di <strong>Firebase Authentication</strong><br />
            • Kurir login di halaman <code style={{ background: '#dcfce7', padding: '1px 6px', borderRadius: 4 }}>/courier-login</code> pakai <strong>email + password</strong> yang didaftarkan<br />
            • Untuk reset password kurir: Firebase Console → Authentication → cari email → klik ··· → Reset password (kirim email reset)<br />
            • Password admin diatur langsung di Firebase Console → Authentication → Users
          </div>
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr><th>Nama Kurir</th><th>Telepon</th><th>Kendaraan</th><th>Email Login</th><th>Status</th><th>Link Tracking</th><th>Aksi</th></tr>
            </thead>
            <tbody>
              {couriers.map(c => (
                <tr key={c.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: c.status === 'active' ? 'var(--green-700)' : '#e2e8f0', color: c.status === 'active' ? '#fff' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>
                        {c.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600 }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.plateNumber || '-'}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 13 }}>{c.phone || '-'}</td>
                  <td style={{ fontSize: 13 }}>{c.vehicle || 'Motor'}</td>
                  <td>
                    <div style={{ fontSize: 13, color: '#0369a1', fontWeight: 500 }}>{c.email || '-'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                      {c.uid ? <span style={{ color: '#16a34a' }}>✅ Akun aktif</span> : <span style={{ color: '#dc2626' }}>⚠️ Belum ada akun Auth</span>}
                    </div>
                  </td>
                  <td>
                    <button onClick={() => toggleStatus(c)}
                      style={{ padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: c.status === 'active' ? '#dcfce7' : '#f1f5f9', color: c.status === 'active' ? '#16a34a' : '#64748b' }}>
                      {c.status === 'active' ? '● Aktif' : '○ Nonaktif'}
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-sm" onClick={() => copyTrackingLink(c.id)}
                        style={{ background: '#eff6ff', color: '#0369a1', border: '1px solid #bfdbfe', padding: '4px 8px', fontSize: 12 }}>
                        <Link size={12} /> Copy Link
                      </button>
                      <a href={`/tracking/${c.id}`} target="_blank" rel="noreferrer" className="btn btn-sm"
                        style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac', padding: '4px 8px', fontSize: 12 }}>
                        <ExternalLink size={12} />
                      </a>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-sm btn-ghost" onClick={() => openEdit(c)} style={{ padding: '4px 8px' }} title="Edit data"><Pencil size={13} /></button>
                      <button className="btn btn-sm" onClick={() => openChangePass(c)}
                        style={{ padding: '4px 8px', background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }} title="Ganti password">
                        <KeyRound size={13} />
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(c.id)} style={{ padding: '4px 8px' }} title="Hapus"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {couriers.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Belum ada kurir terdaftar</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Modal tambah/edit kurir */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <div className="modal-title">{editId ? '✏️ Edit Data Kurir' : '➕ Tambah Kurir Baru'}</div>
                <button className="modal-close" onClick={() => setShowModal(false)}><X size={18} /></button>
              </div>

              {!editId && (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '10px 14px', marginBottom: '1rem', fontSize: 13, color: '#166534' }}>
                  🔐 Akun login kurir akan <strong>otomatis dibuat</strong> di Firebase Authentication. Kurir langsung bisa login dengan email & password yang kamu isi.
                </div>
              )}
              {editId && (
                <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 14px', marginBottom: '1rem', fontSize: 13, color: '#92400e' }}>
                  ✏️ Mode edit hanya mengubah data profil. Untuk ganti password, tutup modal ini lalu klik tombol 🔑 di baris kurir.
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
                {[
                  { label: 'Nama Lengkap *', key: 'name', placeholder: 'Budi Santoso', span: 2 },
                  { label: 'Nomor Telepon', key: 'phone', placeholder: '081234567890', type: 'tel' },
                  { label: 'Jenis Kendaraan', key: 'vehicle', placeholder: 'Motor / Mobil' },
                  { label: 'Plat Nomor', key: 'plateNumber', placeholder: 'L 1234 AB' },
                  { label: 'Email Login *', key: 'email', placeholder: 'budi@qurban.id', type: 'email', disabled: !!editId },
                ].map(f => (
                  <div key={f.key} className="form-group" style={f.span ? { gridColumn: `span ${f.span}` } : {}}>
                    <label className="form-label">{f.label}</label>
                    <input type={f.type || 'text'} className="form-input" placeholder={f.placeholder}
                      value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                      disabled={f.disabled}
                      style={f.disabled ? { background: '#f8fafc', color: '#94a3b8' } : {}} />
                  </div>
                ))}

                {!editId && (
                  <div className="form-group">
                    <label className="form-label">Password Login *</label>
                    <div style={{ position: 'relative' }}>
                      <input type={showPass ? 'text' : 'password'} className="form-input"
                        placeholder="Min. 6 karakter" style={{ paddingRight: 40 }}
                        value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
                      <button type="button" onClick={() => setShowPass(!showPass)}
                        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                        {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {form.password && (
                      <div style={{ marginTop: 4, fontSize: 12, color: form.password.length >= 6 ? '#16a34a' : '#dc2626' }}>
                        {form.password.length >= 6 ? '✅ Password cukup kuat' : `❌ Masih kurang ${6 - form.password.length} karakter`}
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Status Tugas</label>
                  <select className="form-input form-select" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                    <option value="inactive">○ Nonaktif / Istirahat</option>
                    <option value="active">● Aktif Bertugas</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Batal</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? '⏳ Memproses...' : editId ? 'Simpan Perubahan' : '🔐 Buat Akun & Simpan'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal ganti password */}
        {showPassModal && selectedCourier && (
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth: 400 }}>
              <div className="modal-header">
                <div className="modal-title">🔑 Reset Password Kurir</div>
                <button className="modal-close" onClick={() => setShowPassModal(false)}><X size={18} /></button>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--green-50)', borderRadius: 10, marginBottom: '1rem' }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--green-700)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                    {selectedCourier.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: 'var(--green-900)' }}>{selectedCourier.name}</div>
                    <div style={{ fontSize: 13, color: '#0369a1' }}>{selectedCourier.email}</div>
                  </div>
                </div>

                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#92400e', lineHeight: 1.7 }}>
                  <strong>Cara reset password kurir via Firebase Console:</strong><br />
                  1. Buka <strong>Firebase Console</strong><br />
                  2. Pilih project → <strong>Authentication → Users</strong><br />
                  3. Cari email: <code style={{ background: '#fef3c7', padding: '1px 5px', borderRadius: 3 }}>{selectedCourier.email}</code><br />
                  4. Klik ··· → <strong>"Reset password"</strong> → kirim email reset<br />
                  5. Atau klik <strong>"Edit user"</strong> → ubah password langsung
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost" onClick={() => setShowPassModal(false)}>Tutup</button>
                <a href="https://console.firebase.google.com" target="_blank" rel="noreferrer" className="btn btn-primary">
                  Buka Firebase Console ↗
                </a>
              </div>
            </div>
          </div>
        )}

        {toast && (
          <div className="toast-container">
            <div className={`toast toast-${toast.type}`} style={{ maxWidth: 400 }}>
              {toast.msg}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
